import React, { useEffect, useState, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Search, X, ChevronRight, Users, Shield, ShieldOff, Lock, Unlock, LogOut } from 'lucide-react';
import { statusLabel } from './statusLabels';
import { useAuth } from '../../context/AuthContext';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

// Matches the server default (users.js). Keeps the visible page and the "showing
// X of N" line in step with what the backend actually returns.
const PAGE_SIZE = 50;

const STATUS_COLORS = {
  pending_payment: theme.state.pending,
  paid:            theme.state.success,
  picked_up:       theme.state.info,
  returned:        theme.state.neutral,
  late:            theme.state.danger,
  cancelled:       theme.state.muted,
  disputed:        theme.state.dispute,
  lost:            theme.state.lost,
};

const roleLabel = (t, role) => {
  if (role === 'admin')    return t('admin.customers.roleAdmin');
  if (role === 'customer') return t('admin.customers.roleCustomer');
  return role;
};

// The interceptor flattens the error body, so `error` is the machine code and
// `message` the sentence worth showing. The account routes answer 409 with a
// named code — LAST_ADMIN, SELF_TARGET, ACCOUNT_DELETED — plus a message that
// explains the refusal, and that message is the whole value of the response.
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

export default function AdminCustomers() {
  const { t } = useTranslation();
  // Needed to know which row is the operator's own account: the server refuses
  // to let anyone change their own role or lock themselves out, and a button
  // that only ever produces a 409 should not be offered in the first place.
  const { user: currentUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [count, setCount]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [detail, setDetail]       = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError]     = useState(false);

  const load = useCallback(async (q, pageArg) => {
    setLoading(true);
    try {
      // Endpoint is paginated and returns { users, count, limit, offset }. The
      // list previously showed only the first server page and printed its length
      // as the total, so customer #51+ could not be seen at all (audit H5/#43).
      // The array fallback keeps this working against an older backend.
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (pageArg - 1) * PAGE_SIZE });
      if (q) params.set('search', q);
      const data = await api.get(`/users?${params}`);
      const rows = Array.isArray(data) ? data : data.users || [];
      setCustomers(rows);
      setCount(Number(data?.count) || rows.length);
    } catch (err) {
      toast.error(t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // A new search starts again from the first page.
  useEffect(() => { setPage(1); }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => load(search, page), 300);
    return () => clearTimeout(timer);
  }, [search, page, load]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const openDetail = useCallback(async (id) => {
    setDetail(id);
    setDetailData(null);
    setDetailError(false);
    setDetailLoading(true);
    try {
      const data = await api.get(`/users/${id}`);
      setDetailData(data);
    } catch (err) {
      // try/finally with no catch left detailData null, and the drawer renders
      // "Loading…" for exactly that state — so a 500 or a dropped connection
      // looked like a request that never came back. Show the failure and offer
      // the retry, rather than presenting an outage as a spinner.
      setDetailError(true);
      toast.error(err?.error || t('admin.customers.detailLoadFailed'));
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  const closeDetail = () => { setDetail(null); setDetailData(null); setDetailError(false); };

  // ── Account actions ───────────────────────────────────────────────────────
  //
  // This page could read the user list and nothing else, so appointing a second
  // operator, stopping an account that is abusing us, or ending a session on a
  // stolen laptop all meant opening psql. Each of these writes to the admin
  // action log, and each is refused by the server for its own reasons (last
  // admin, own account, deleted account) — the refusal carries the sentence
  // explaining why, which is what gets shown.
  const runAction = async (request, successMessage, fallbackError) => {
    setActionBusy(true);
    try {
      const updated = await request();
      // Patch the open drawer in place so the badge and the buttons flip
      // immediately, then re-read the list underneath it.
      setDetailData(prev => (prev ? { ...prev, user: { ...prev.user, ...updated } } : prev));
      toast.success(successMessage);
      load(search, page);
    } catch (err) {
      toast.error(serverText(err, fallbackError), { duration: 8000 });
    } finally {
      setActionBusy(false);
    }
  };

  const changeRole = (user) => {
    const promoting = user.role !== 'admin';
    if (!window.confirm(promoting
      ? t('admin.customers.promoteConfirm', { name: user.full_name || user.email })
      : t('admin.customers.demoteConfirm', { name: user.full_name || user.email }))) return;
    runAction(
      () => api.patch(`/users/${user.id}/role`, { role: promoting ? 'admin' : 'customer' }),
      promoting ? t('admin.customers.promoted') : t('admin.customers.demoted'),
      t('admin.customers.roleChangeFailed'),
    );
  };

  const toggleLock = (user) => {
    const locking = !user.locked_at;
    let reason = null;
    if (locking) {
      reason = window.prompt(t('admin.customers.lockPrompt', { name: user.full_name || user.email }));
      // Cancel is null and means "changed my mind".
      if (reason === null) return;
      // An empty box used to be allowed through as "no reason given". The
      // customer is now emailed the reason — it is the grounds they would
      // contest — so there has to be one. The server rejects it too.
      if (!reason.trim()) {
        toast.error(t('admin.customers.lockReasonRequired'), { duration: 6000 });
        return;
      }
    } else if (!window.confirm(t('admin.customers.unlockConfirm'))) {
      return;
    }
    runAction(
      () => api.patch(`/users/${user.id}/lock`, { locked: locking, reason }),
      locking ? t('admin.customers.locked') : t('admin.customers.unlocked'),
      t('admin.customers.lockFailed'),
    );
  };

  const revokeSessions = (user) => {
    if (!window.confirm(t('admin.customers.revokeConfirm', { name: user.full_name || user.email }))) return;
    runAction(
      // Answers { message, id } rather than a user row, so there is nothing to
      // merge into the drawer — the toast is the whole result.
      () => api.post(`/users/${user.id}/revoke-sessions`).then(() => ({})),
      t('admin.customers.revoked'),
      t('admin.customers.revokeFailed'),
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      <style>{`@import url('${FONTS}');`}</style>
      <AdminSidebar />

      <main className="admin-main" style={{ padding: '40px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 36, fontWeight: 400, color: theme.ink, margin: 0 }}>{t('admin.customers.title')}</h1>
            <p style={{ color: theme.grey400, marginTop: 4, fontSize: 13 }}>{t('admin.customers.count', { count })}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '10px 16px' }}>
            <Search size={14} color={theme.grey400} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('admin.customers.searchPlaceholder')}
              style={{ border: 'none', outline: 'none', fontSize: 13, background: 'transparent', width: 200, fontFamily: theme.font.body }}
            />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={14} color={theme.grey400} /></button>}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: theme.gold, fontFamily: theme.font.display, fontSize: 24 }}>{t('admin.customers.loading')}</div>
        ) : (
          <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: theme.creamDeep, borderBottom: `1px solid ${theme.sand}` }}>
                  {[t('admin.customers.colCustomer'), t('admin.customers.colPhone'), t('admin.customers.colRentals'), t('admin.customers.colSpent'), t('admin.customers.colRegistered'), ''].map(h => (
                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 500, color: theme.grey500, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${theme.creamBone}` }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.cream}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, color: theme.ink }}>{c.full_name || '—'}</span>
                        {/* A suspended account cannot book and cannot sign in.
                            That has to be visible in the list, not only after
                            someone opens the drawer to find out why a customer
                            says the site is broken for them. */}
                        {c.locked_at && (
                          <span style={{ background: theme.state.danger.bg, color: theme.state.danger.color, fontSize: 10, padding: '2px 9px', borderRadius: 100, letterSpacing: '0.05em' }}>
                            {t('admin.customers.badgeLocked')}
                          </span>
                        )}
                        {c.role === 'admin' && (
                          <span style={{ background: theme.goldWash, color: theme.goldInk, fontSize: 10, padding: '2px 9px', borderRadius: 100, letterSpacing: '0.05em' }}>
                            {t('admin.customers.roleAdmin')}
                          </span>
                        )}
                      </div>
                      <div style={{ color: theme.grey400, fontSize: 12, marginTop: 2 }}>{c.email}</div>
                    </td>
                    <td style={{ padding: '14px 16px', color: theme.grey600 }}>{c.phone || '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontFamily: theme.font.display, fontSize: 20, color: parseInt(c.total_rentals) > 0 ? theme.ink : theme.grey240 }}>
                        {c.total_rentals}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', color: theme.ink, fontWeight: 500 }}>
                      €{parseFloat(c.total_spent).toFixed(2)}
                    </td>
                    <td style={{ padding: '14px 16px', color: theme.grey400, fontSize: 12 }}>
                      {format(new Date(c.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button onClick={() => openDetail(c.id)}
                        style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: theme.grey600 }}>
                        {t('admin.customers.details')} <ChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.customers.noCustomers')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && count > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.customers.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, count),
                total: count,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.customers.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= count}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= count ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= count ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.customers.next')}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Detail drawer */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.4), zIndex: 200 }} onClick={closeDetail}>
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 480, background: theme.white, boxShadow: `-4px 0 24px ${theme.alpha.black(0.1)}`, overflowY: 'auto', padding: 32 }}
            onClick={e => e.stopPropagation()}>
            <button onClick={closeDetail}
              style={{ float: 'right', background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: theme.grey400 }}>×</button>

            {detailError ? (
              <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                <p style={{ color: theme.redDark, fontSize: 14, marginBottom: 20 }}>{t('admin.customers.detailLoadFailed')}</p>
                <button onClick={() => openDetail(detail)}
                  style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 2, padding: '10px 24px', cursor: 'pointer', fontSize: 13, color: theme.grey600, fontFamily: theme.font.body }}>
                  {t('admin.customers.detailRetry')}
                </button>
              </div>
            ) : detailLoading || !detailData ? (
              <div style={{ textAlign: 'center', padding: 80, color: theme.gold, fontFamily: theme.font.display, fontSize: 22 }}>{t('admin.customers.detailLoading')}</div>
            ) : (
              <>
                <div style={{ marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: theme.creamDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Users size={20} color={theme.gold} />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: theme.font.display, fontSize: 24, margin: 0 }}>{detailData.user.full_name || '—'}</h2>
                      <div style={{ color: theme.grey400, fontSize: 13 }}>{detailData.user.email}</div>
                    </div>
                  </div>
                  <div style={{ background: theme.creamDeep, borderRadius: 4, padding: '14px 18px', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: theme.grey400 }}>{t('admin.customers.labelPhone')}</span>
                      <span>{detailData.user.phone || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: theme.grey400 }}>{t('admin.customers.labelRegistered')}</span>
                      <span>{format(new Date(detailData.user.created_at), 'dd/MM/yyyy')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.grey400 }}>{t('admin.customers.labelRole')}</span>
                      <span style={{ color: detailData.user.role === 'admin' ? theme.gold : theme.ink }}>{roleLabel(t, detailData.user.role)}</span>
                    </div>
                  </div>
                </div>

                {/* ── Account actions ─────────────────────────────────── */}
                {(() => {
                  const u = detailData.user;
                  const isSelf = currentUser?.id === u.id;
                  const btn = (extra = {}) => ({
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '11px 14px', borderRadius: 4, fontSize: 12.5,
                    fontFamily: theme.font.body, textAlign: 'left',
                    background: theme.white, border: `1px solid ${theme.sand}`,
                    color: theme.grey600,
                    cursor: actionBusy ? 'wait' : 'pointer',
                    opacity: actionBusy ? 0.55 : 1,
                    ...extra,
                  });
                  return (
                    <div style={{ marginBottom: 28 }}>
                      <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 12 }}>
                        {t('admin.customers.actionsSection')}
                      </h3>

                      {u.locked_at && (
                        <div style={{ background: theme.redWashPale, border: `1px solid ${theme.redBorder}`, borderRadius: 4, padding: '11px 14px', fontSize: 12.5, color: theme.redSoft, marginBottom: 12, lineHeight: 1.6 }}>
                          {u.locked_reason
                            ? t('admin.customers.lockedBecause', { reason: u.locked_reason })
                            : t('admin.customers.lockedNoReason')}
                          {u.locked_by_name && <> · {t('admin.customers.lockedBy', { name: u.locked_by_name })}</>}
                          {' · '}{format(new Date(u.locked_at), 'dd/MM/yyyy HH:mm')}
                        </div>
                      )}

                      {isSelf ? (
                        // The server refuses both of these on your own account,
                        // so say why here rather than offering a button whose
                        // only possible outcome is a 409.
                        <div style={{ background: theme.creamDeep, borderRadius: 4, padding: '11px 14px', fontSize: 12.5, color: theme.grey600, marginBottom: 12, lineHeight: 1.6 }}>
                          {t('admin.customers.selfNote')}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                          <button disabled={actionBusy} onClick={() => changeRole(u)}
                            style={btn(u.role === 'admin' ? {} : { color: theme.goldInk, borderColor: theme.sandTan })}>
                            {u.role === 'admin' ? <ShieldOff size={14} /> : <Shield size={14} />}
                            {u.role === 'admin' ? t('admin.customers.demote') : t('admin.customers.promote')}
                          </button>

                          <button disabled={actionBusy} onClick={() => toggleLock(u)}
                            style={btn(u.locked_at ? { color: theme.greenDeep, borderColor: theme.greenDeep } : { color: theme.redBrick, borderColor: theme.redBorder })}>
                            {u.locked_at ? <Unlock size={14} /> : <Lock size={14} />}
                            {u.locked_at ? t('admin.customers.unlock') : t('admin.customers.lock')}
                          </button>
                        </div>
                      )}

                      {/* Allowed on your own account, and that is the point when
                          a laptop goes missing. */}
                      <button disabled={actionBusy} onClick={() => revokeSessions(u)} style={btn()}>
                        <LogOut size={14} /> {t('admin.customers.revoke')}
                      </button>
                      <div style={{ fontSize: 11, color: theme.grey400, marginTop: 8, lineHeight: 1.6 }}>
                        {t('admin.customers.revokeHelp')}
                      </div>
                    </div>
                  );
                })()}

                <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 16 }}>
                  {t('admin.customers.rentalsSection', { count: detailData.rentals.length })}
                </h3>

                {detailData.rentals.length === 0 ? (
                  <div style={{ color: theme.grey400, fontSize: 13, textAlign: 'center', padding: 24 }}>{t('admin.customers.noRentals')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {detailData.rentals.map(r => {
                      const sc = STATUS_COLORS[r.status] || { bg: theme.grey100, color: theme.grey600 };
                      return (
                        <div key={r.id} style={{ border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '14px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontWeight: 500, fontSize: 14, color: theme.ink }}>{r.product_name}</span>
                            <span style={{ background: sc.bg, color: sc.color, fontSize: 11, padding: '2px 8px', borderRadius: 10, letterSpacing: '0.05em' }}>{statusLabel(t, r.status)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.grey400 }}>
                            <span>{t('admin.customers.rentalDays', { count: r.rental_days })} · €{parseFloat(r.total_rental_price).toFixed(2)}</span>
                            <span>{format(new Date(r.created_at), 'dd/MM/yyyy')}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
